import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { RewardInterface } from '../../../home/domain/interface/reward_interface';
import { useOutletContext } from 'react-router-dom';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { useClaimReward } from '../../domain/hooks/use_claim_reward';
import { PulseLoader } from 'react-spinners';
import LocationIcon from '../../../../assets/images/location_icon.svg?react';
import UvibesPointIcon from '../../../../assets/images/uvibes_point.svg?react';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../../../i18n/useTranslation';
import { Trans } from 'react-i18next';
import { getCurrentLocale } from '../../../../i18n/config';


interface ExclusiveRewardDialogProps {
    onClose: () => void;
    reward: RewardInterface | null;
}

const getRewardDate = (t: TFunction, startDate?: Date, endDate?: Date): string => {
    const formatDate = (date: Date): string =>
        date.toLocaleDateString(getCurrentLocale(), { day: 'numeric', month: 'long' });

    if (startDate && endDate) {
        return t('rewards.date_range', { start: formatDate(startDate), end: formatDate(endDate) });
    } else if (startDate) {
        return t('rewards.date_from', { start: formatDate(startDate) });
    } else if (endDate) {
        return t('rewards.date_until', { end: formatDate(endDate) });
    }
    return '';
}

const ExclusiveRewardDialog: React.FC<ExclusiveRewardDialogProps> = ({ onClose, reward }) => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const { firstname } = useOutletContext<UserInterface>();
    const { mutate, isPending } = useClaimReward();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const handleConfirmClaim = () => {
        if (!reward?.userReward.id) return;
        mutate(reward.userReward.id, {
            onSuccess: () => {
                setIsConfirmOpen(false);
                onClose();
            }
        });
    };

    return (
        <>
            <Dialog
                fullWidth={true}
                onClose={onClose}
                open={reward !== null}
                slotProps={{
                    backdrop: {
                        style: {
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        },
                    },
                    container: {
                        className: '!w-screen'
                    },
                    paper: {
                        className: '!rounded-[20px] !m-3 !max-w-[378px]',
                    },
                }}
            >
                {reward && (
                    (reward.expGained || 0) >= reward.level && !reward.userReward.earned && reward.userReward.id && reward.address ? (
                        <div className='flex flex-col items-center gap-18 w-full bg-white px-5 py-4.5'>
                            <div className='flex flex-col items-center gap-9 w-full'>
                                <div className='w-full flex justify-end'>
                                    <svg 
                                        width="24" 
                                        height="24" 
                                        viewBox="0 0 24 24" 
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="cursor-pointer"
                                        onClick={onClose}
                                    >
                                        <g>
                                            <path d="M12 13.3998L7.1 18.2998C6.91667 18.4831 6.68334 18.5748 6.4 18.5748C6.11667 18.5748 5.88334 18.4831 5.7 18.2998C5.51667 18.1165 5.425 17.8831 5.425 17.5998C5.425 17.3165 5.51667 17.0831 5.7 16.8998L10.6 11.9998L5.7 7.0998C5.51667 6.91647 5.425 6.68314 5.425 6.3998C5.425 6.11647 5.51667 5.88314 5.7 5.6998C5.88334 5.51647 6.11667 5.4248 6.4 5.4248C6.68334 5.4248 6.91667 5.51647 7.1 5.6998L12 10.5998L16.9 5.6998C17.0833 5.51647 17.3167 5.4248 17.6 5.4248C17.8833 5.4248 18.1167 5.51647 18.3 5.6998C18.4833 5.88314 18.575 6.11647 18.575 6.3998C18.575 6.68314 18.4833 6.91647 18.3 7.0998L13.4 11.9998L18.3 16.8998C18.4833 17.0831 18.575 17.3165 18.575 17.5998C18.575 17.8831 18.4833 18.1165 18.3 18.2998C18.1167 18.4831 17.8833 18.5748 17.6 18.5748C17.3167 18.5748 17.0833 18.4831 16.9 18.2998L12 13.3998Z" fill="black"/>
                                        </g>
                                    </svg>
                                </div>
                                <div className='flex flex-col items-center gap-4 w-full'>
                                    <h3 className='text-xl font-medium text-center'>{t('rewards.claim_title')}</h3>
                                    <div className='flex flex-col items-center gap-1.5 w-full'>
                                        <span className='text-xs text-center'>{t('rewards.claim_place')}</span>
                                        <div className='flex items-center justify-center gap-1.5 w-full'>
                                            <LocationIcon className="h-6 w-auto shrink-0"/>
                                            <span className='text-xs text-center font-bold min-w-0 wrap-anywhere'>{reward.address}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='flex flex-col items-center gap-5 w-full'>
                                <p className='text-sm font-bold text-center leading-snug'>{t('rewards.claim_hint')}</p>
                                <button
                                    className={`rounded-[10px] w-full px-4.75 py-2.25 ${isPending ? 'cursor-not-allowed' : 'cursor-pointer'} text-white font-bold text-sm`}
                                    style={{ backgroundColor: theme.UvibesPink }}
                                    disabled={isPending}
                                    onClick={() => setIsConfirmOpen(true)}
                                >
                                    {isPending ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : t('rewards.claim_confirm', { firstname })}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col items-center gap-23.5 w-full bg-white px-5 py-4.5'>
                            <div className='flex flex-col items-center gap-4 w-full'>
                                <div className='w-full flex justify-end'>
                                    <svg 
                                        width="24" 
                                        height="24" 
                                        viewBox="0 0 24 24" 
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="cursor-pointer"
                                        onClick={onClose}
                                    >
                                        <g>
                                            <path d="M12 13.3998L7.1 18.2998C6.91667 18.4831 6.68334 18.5748 6.4 18.5748C6.11667 18.5748 5.88334 18.4831 5.7 18.2998C5.51667 18.1165 5.425 17.8831 5.425 17.5998C5.425 17.3165 5.51667 17.0831 5.7 16.8998L10.6 11.9998L5.7 7.0998C5.51667 6.91647 5.425 6.68314 5.425 6.3998C5.425 6.11647 5.51667 5.88314 5.7 5.6998C5.88334 5.51647 6.11667 5.4248 6.4 5.4248C6.68334 5.4248 6.91667 5.51647 7.1 5.6998L12 10.5998L16.9 5.6998C17.0833 5.51647 17.3167 5.4248 17.6 5.4248C17.8833 5.4248 18.1167 5.51647 18.3 5.6998C18.4833 5.88314 18.575 6.11647 18.575 6.3998C18.575 6.68314 18.4833 6.91647 18.3 7.0998L13.4 11.9998L18.3 16.8998C18.4833 17.0831 18.575 17.3165 18.575 17.5998C18.575 17.8831 18.4833 18.1165 18.3 18.2998C18.1167 18.4831 17.8833 18.5748 17.6 18.5748C17.3167 18.5748 17.0833 18.4831 16.9 18.2998L12 13.3998Z" fill="black"/>
                                        </g>
                                    </svg>
                                </div>
                                <div className='flex flex-col items-center gap-3.5 w-full'>
                                    {reward.picture && <img className='max-h-55 max-w-full object-cover rounded-[10px]' src={reward?.picture}/>}
                                    <div className='flex flex-col items-start w-full gap-0.75'>
                                        <h3 className='font-bold text-sm leading-tight w-full wrap-anywhere'>{reward.name}</h3>
                                        <span className='text-xs leading-tight'>{getRewardDate(t, reward.startDate, reward.endDate)}</span>
                                    </div>
                                    {reward.description && <p className='text-xs leading-relaxed w-full wrap-anywhere'>{reward.description}</p>}
                                </div>
                            </div>
                            <button
                                className="rounded-[10px] flex px-4.75 py-2.25 gap-1.5 items-center text-white font-bold text-sm"
                                style={{ backgroundColor: theme.UvibesPink }}
                            >
                                <span>{(reward.expGained || 0) >= reward.level ? t('rewards.claimed') : t('rewards.locked')}</span>
                            
                                <UvibesPointIcon className="h-4.5 w-auto"/>
                                <span>{`${Math.min(reward.level, (reward.expGained || 0))}/${reward.level}`}</span>
                            </button>
                        </div>
                    )
                )}
            </Dialog>
            <Dialog
                fullWidth={true}
                onClose={() => !isPending && setIsConfirmOpen(false)}
                open={isConfirmOpen && reward !== null}
                slotProps={{
                    backdrop: {
                        style: {
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        },
                    },
                    container: {
                        className: '!w-screen'
                    },
                    paper: {
                        className: '!rounded-[20px] !m-3 !max-w-[378px]',
                    },
                }}
            >
                <div className='flex flex-col items-center gap-7 w-full bg-white px-7 pt-10 pb-8'>
                    <p className='text-sm font-medium text-center leading-relaxed text-black'><Trans i18nKey="rewards.claim_warning" /></p>
                    <div className='flex flex-col items-center gap-3.75 w-full'>
                        <button
                            className={`rounded-[10px] w-full px-4.75 py-2.25 text-white font-bold text-sm ${isPending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={handleConfirmClaim}
                            disabled={isPending}
                        >
                            {isPending ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : t('common.button.confirm')}
                        </button>
                        <button
                            className={`rounded-[10px] w-full px-4.75 py-2.25 font-bold text-sm ${isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            style={{
                                color: theme.UvibesPink,
                                boxShadow: `inset 0 0 0 1px ${theme.UvibesPink}`
                            }}
                            onClick={() => setIsConfirmOpen(false)}
                            disabled={isPending}
                        >
                            {t('common.button.cancel')}
                        </button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default ExclusiveRewardDialog;